import { timeElapsed } from '@playbooks/utils/dates';
import { env, isArray, isEmpty, isObject, sleep } from '@playbooks/utils/helpers';
import { logger } from '@playbooks/utils/logger';

import fetch from 'cross-fetch';
import Https from 'https';

export type ApiError = {
	status?: number;
	statusCode?: number;
	title?: string;
	description?: string;
	message?: string;
	code?: string;
	[key: string]: any;
};

type BaseAdapterOptions = {
	domain: string;
	onError?: (error: ApiError) => void;
};

export type requestProps = {
	method?: string;
	url?: string;
	headers?: any;
	params?: any;
	data?: any | any[];
	signal?: AbortSignal;
};

class BaseAdapter {
	domain: string;
	onError?: (error: ApiError) => void;

	constructor({ domain, onError }: BaseAdapterOptions) {
		this.domain = domain;
		this.onError = onError;
	}

	async client(url, options) {
		const response = await fetch(url, options);
		if (!response.ok) {
			const data = await response.json();
			const error = data.error || data;
			this.onError?.(error);
			throw error;
		}
		return await response.json();
	}

	formatUrl(url: string, params: any): URL {
		const formattedUrl = new URL(this.domain + url);
		Object.keys(params || {}).map(key => {
			const value = params[key];
			if (isEmpty(value)) return;
			if (isArray(value)) return formattedUrl.searchParams.append(key, value.join(','));
			if (isObject(value)) return Object.keys(value).map(key => formattedUrl.searchParams.append(key, value[key]));
			return formattedUrl.searchParams.append(key, value);
		});
		return formattedUrl;
	}

	formatOptions(method, headers, data?, signal?) {
		const formattedOptions = {};
		const formData = typeof FormData !== 'undefined' && data instanceof FormData;
		formattedOptions['method'] = method;
		formattedOptions['credentials'] = 'include';
		formattedOptions['headers'] = formData ? { ...headers } : { ['Content-Type']: 'application/json', ...headers };
		formattedOptions['body'] = formData ? data : JSON.stringify(data);
		if (signal) formattedOptions['signal'] = signal;
		if (env === 'development') formattedOptions['agent'] = new Https.Agent({ rejectUnauthorized: false });
		return formattedOptions;
	}

	formatRequest({ method = 'GET', url, headers, params, data, signal }) {
		const date = new Date();
		const formattedUrl = this.formatUrl(url, params);
		const formattedOptions = this.formatOptions(method, headers, data, signal);
		return { date, formattedUrl, formattedOptions };
	}

	async request({ method = 'GET', url, headers, params, data, signal }: requestProps) {
		const { date, formattedUrl, formattedOptions } = this.formatRequest({ method, url, headers, params, data, signal });
		const response = await this.client(formattedUrl, formattedOptions);
		return [timeElapsed(date), response];
	}

	async apiRequest({ method = 'GET', url, headers, params, data, signal }: requestProps) {
		logger.info(`apiRequest: `, { method, url, params, data });
		const [date, response] = await this.request({ method, url, headers, params, data, signal });
		logger.info(`apiResponse (${date}): `, { method, url, params, response });
		return response;
	}

	async storeRequest({ method = 'GET', url, headers, params, data, signal }: requestProps) {
		if (env === 'development') await sleep(300);
		logger.info(`storeRequest: `, { method, url, params, data });
		const [date, response] = await this.request({ method, url, headers, params, data, signal });
		logger.info(`storeResponse (${date}): `, { method, url, params, response });
		return response;
	}

	async downloadRequest({ method = 'GET', url, headers, params, data, signal }: requestProps) {
		if (env === 'development') await sleep(300);
		logger.info(`downloadRequest: `, { method, url, params, data });
		const { date, formattedUrl, formattedOptions } = this.formatRequest({ method, url, headers, params, data, signal });
		const response = await fetch(formattedUrl, formattedOptions);
		logger.info(`downloadResponse (${timeElapsed(date)}): `, { method, url, params, response });
		if (!response.ok) {
			const data = await response.json();
			const error = data.error || data;
			this.onError?.(error);
			throw error;
		}
		return await response;
	}
}

export { BaseAdapter };

// Docs:
// https://github.github.io/fetch/
// https://www.npmjs.com/package/cross-fetch#usage
// https://developer.mozilla.org/en-US/docs/Web/API/URL
// https://developer.mozilla.org/en-US/docs/Web/API/URLSearchParams
